import { AppForms, React, variantProvider } from '@/app'
import { ActivityIndicator, AuthFormComponents, Button, Checkbox, Link, Text, TextInput, View } from '@/components'
import { AppStatus, ModalName, useAppSelector } from '@/redux'
import { APIClient } from '@/services'
import { useIsMobile, useKeydown } from '@/utils'
import { assignTextStyle, useForm, useI18N } from '@codeleap/common'
import { navigate } from 'gatsby'
import { useAuth } from '@/features/Auth/bridge/useAuth'
import { useSignup } from '@/features/Auth/bridge/useSignup'
import { createSignupForm } from '@/features/Auth/forms/signup'

type SignupFormProps = {
  isModal?: boolean
  title?: string
  modalName?: ModalName
  referral_code?: string
}

const BREAKPOINT = 'mid'

export const SignupForm = (props: SignupFormProps) => {
  const { isModal = true, referral_code = '', modalName = 'signup' } = props

  const isFocused = useAppSelector(state => state?.AppStatus?.modals?.signup)

  // The form renders what the machine says is true; it no longer keeps its own idea of how
  // this signup started. `isSocial` was derived from a piece of local state set by a
  // callback, and the form config was picked from it — two sources for one question, which
  // is how a prefilled form could still be asking for a password. See EPIC-005.
  const { registration } = useAuth()
  const source = registration?.provider ?? 'email'
  const needsPassword = registration ? registration.needsPassword : true

  const isMobile = useIsMobile()

  const areFieldsVisible = isModal ? isFocused : true
  const formConfig = createSignupForm(needsPassword, areFieldsVisible)
  const form = useForm(formConfig, { initialState: { referral_code } })

  const referralCode = APIClient.Session.useReferralCode(form.values.referral_code)

  React.useEffect(() => {
    if (referral_code) form.setFieldValue('referral_code', referral_code)
  }, [referral_code])

  // The details travel on the state, so the form reads them rather than being handed them by
  // whoever obtained the credential. Google and Apple return no address with the credential —
  // it is read off the Firebase user — and losing it left this form with an address field
  // that was empty and disabled at once, which cannot be submitted or corrected.
  React.useEffect(() => {
    if (!registration) return

    const { displayName, email } = registration.prefill

    if (displayName) form.setFieldValue('name', displayName as never)
    if (email) form.setFieldValue('email', email as never)
  }, [registration?.prefill?.uid])

  const { t } = useI18N()

  const { signup } = useSignup()

  const handleSubmit = async () => {
    if (!referralCode.isValid) return

    // `useForm` infers its value type from `initialState` rather than from the config, so
    // `form.values` types as `{ referral_code }` alone — a pre-existing hole in the form
    // helper, not something this form can state correctly.
    const { name, email, ...rest } = form.values as unknown as { name: string; email: string }
    const firstName = name.split(' ').slice(0, 1).join(' ')
    const lastName = name.split(' ').slice(1).join(' ')
    const values = { ...rest, email: email.toLowerCase(), first_name: firstName, last_name: lastName }

    await signup(values as never, source)
  }

  const openLogin = () => {
    if (isModal) {
      AppStatus.transitionModals([modalName, 'login'])
    } else {
      navigate('/auth/login/')
    }
  }

  useKeydown(
    () => {
      const isInvalidSubmit = (isModal && !isFocused) || !form.isValid
      if (isInvalidSubmit) return
      handleSubmit?.()
    },
    [isFocused, isModal, form?.values],
    'enter'
  )

  return (
    <>
      <View variants={['flex', 'column', 'gap:4', 'fullWidth', 'marginTop:2']}>
        <View variants={['column', 'gap:1']}>
          <View variants={['row', 'gap:4']} responsiveVariants={{ [BREAKPOINT]: ['column', 'gap:0'] }}>
            <TextInput {...form.register('name')} />
            <TextInput {...form.register('email')} leftIcon={{ name: 'mail' }} disabled={!needsPassword} />
          </View>
          {needsPassword ? (
            <View variants={['row', 'gap:4', 'alignEnd']} responsiveVariants={{ [BREAKPOINT]: ['column', 'gap:0'] }}>
              <TextInput {...form.register('password')} leftIcon={{ name: 'key' }} visibilityToggle />
              <TextInput {...form.register('repeatPassword')} leftIcon={{ name: 'key' }} visibilityToggle />
            </View>
          ) : null}
          <View variants={['row', 'gap:4']} responsiveVariants={{ [BREAKPOINT]: ['column', 'gap:0'] }}>
            <TextInput
              {...form.register('referral_code')}
              leftIcon={{ name: 'form-input' }}
              debugName={'Referral code input'}
              styles={{ wrapper: styles.singleInput }}
              _error={!referralCode.isValid && t('form.validations.invalidReferralCode')}
              rightIcon={
                referralCode.isLoading && !referralCode.isValid ? (
                  <ActivityIndicator variants={['medium']} />
                ) : undefined
              }
            />

            {!isMobile ? <View style={{ width: '50%' }} /> : null}
          </View>

          <Checkbox {...form.register('receive_marketing_email')} variants={['left']} />
        </View>

        <View variants={['alignSelfCenter']}>
          <Text variants={['p3', 'color:neutral8']}>
            {t('Login.disclaimer')}
            <Link to="https://hurd.world/terms-of-use" text={t('termsOfUse')} style={styles.link} tabIndex={0} />
            {', '}
            <Link to="https://hurd.world/privacy-policy" text={t('privacyPolicy')} style={styles.link} tabIndex={0} />
            {t('Login.disclaimer2')}
            <Link
              to="https://hurd.world/community-guidelines"
              text={' ' + t('communityGuidelines')}
              style={styles.link}
              tabIndex={0}
            />
          </Text>
        </View>

        <AuthFormComponents.SocialProviders />

        <View variants={['column', 'gap:2', 'alignSelfCenter']}>
          <Button
            onPress={handleSubmit}
            disabled={!form.isValid || !referralCode.isValid}
            debugName={`submit Signup`}
            text={t('SignUp.createAccount')}
            variants={[
              'marginHorizontal:auto',
              'fullWidth',
              'large',
              'pill',
              'paddingHorizontal:2',
              !form.isValid && 'outline'
            ]}
            aria-label={t('SignUp.createAccount')}
            aria-invalid={!form.isValid}
          />
          <Button
            aria-label={`${t('SignUp.txt4')} ${t('SignUp.login')}`}
            onPress={openLogin}
            debugName={'Already have account Button'}
            variants={[
              'outline',
              'pill',
              'large',
              'row',
              'gap:0.5',
              'alignCenter',
              'justifyCenter',
              'paddingHorizontal:2',
              'bg:transparent'
            ]}
          >
            <Text text={t('SignUp.txt4')} variants={['medium', 'alignSelfCenter', 'row', 'color:neutral10']} />
            <Text text={t('SignUp.login')} variants={['medium', 'color:primary3', 'inline', 'marginRight:0.5']} />
          </Button>
        </View>
      </View>
    </>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    link: {
      ...assignTextStyle('p3')(theme).text,
      color: theme.colors.primary3,
      fontWeight: '400',
      textDecoration: 'none'
    },
    singleInput: {
      width: '50%',

      [theme.media.down(BREAKPOINT)]: {
        width: '100%'
      }
    }
  }),
  true
)
